import { useState } from 'react'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import LeadCaptureModal from '../components/LeadCaptureModal'

/**
 * DebtRecyclingPage — "Pay Off Your Mortgage in 7-10 Years" wealth-strategy page.
 * Content sourced from Greystone's Debt Recycling Client Flyer 2026 (see
 * docs/SaaS/greystone-mockups-light/Logos/Greystone Finance Debt Recycling Flyer 2026.pdf).
 * Every fact/number/caveat on this page is taken verbatim from that flyer — do
 * NOT re-write; regulatory copy should track the flyer exactly.
 */

const STEPS = [
  {
    n: '1',
    title: 'Split your home loan',
    body: 'Set up a separate redraw/split facility marked for investment purposes.',
  },
  {
    n: '2',
    title: 'Pay down the loan',
    body: 'Use surplus cash flow to reduce your home loan balance.',
  },
  {
    n: '3',
    title: 'Redraw to invest',
    body: 'Borrow the same amount via the split to buy income-producing assets.',
  },
  {
    n: '4',
    title: 'Repeat',
    body: 'Non-deductible debt shrinks while deductible debt grows. Total borrowing unchanged.',
  },
]

const PROS = [
  "Same total debt, you don't borrow more, you repurpose what you've got.",
  'Interest on the investment-purpose portion becomes tax-deductible.',
  "Tax savings compound, they're reinvested back into the strategy each year.",
  'Works well with new builds (post-Budget) and shares/ETFs (unaffected by 2026 reforms).',
  'Opens a path to build an investment portfolio alongside paying down the home.',
  'Particularly powerful for households on the 37% or 47% marginal tax rate.',
]

const CONS = [
  'Investment markets can fall, and you still owe the bank.',
  'Requires consistent surplus cash flow to sustain.',
  'Loan structure must be set up properly, sloppy splits can destroy deductibility.',
  'Doesn’t fix bad cash flow, it amplifies whatever financial habits you already have.',
  'ATO purpose-of-funds test must be respected, clear separation between personal and investment debt.',
  'Tax benefit is proportional to marginal rate, much weaker at lower brackets.',
  'Typically a 5+ year strategy, short-horizon households should not start.',
]

const GOOD_FIT = [
  'Home loan + reliable surplus monthly income',
  '37% or 47% marginal tax bracket',
  'Stable employment, 5+ year horizon',
  'Comfortable with investment market volatility',
  'Disciplined with finances, system-driven rather than reactive',
]

const NOT_FIT = [
  'Variable, lumpy or unreliable income',
  'Low marginal tax rate (the tax saving is small)',
  'Need full liquidity from cash holdings',
  'No appetite for investment market swings',
  'Want home loan paid off ASAP for psychological reasons',
]

const WATCH = [
  { area: 'Loan structure', body: "Separate splits from day one. A 'mixed-purpose' loan can lose deductibility entirely." },
  { area: 'Use of funds', body: "Redrawn money must be used directly for income-producing investment. Don't co-mingle with personal spending." },
  { area: 'Investment choice', body: 'Pick income-producing assets (dividend-paying shares, ETFs, new-build property). Speculative or growth-only picks weaken the tax case.' },
  { area: 'Cash flow discipline', body: 'The strategy only works if you keep recycling. Stopping mid-strategy locks in partial benefit but lower ceiling.' },
]

export default function DebtRecyclingPage() {
  const [leadOpen, setLeadOpen] = useState(false)

  return (
    <>
      <Seo
        title="Debt Recycling"
        path="/debt-recycling"
        description="How debt recycling can turn your home loan into a wealth-building strategy, explained by our brokers."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gs-text-soft mb-6">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tenantConfig.accentColor }} />
            Wealth strategy · Client guide
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold leading-tight mb-4">
            Pay off your mortgage in 7-10 years.
          </h1>
          <p className="text-2xl text-gs-text-soft mb-6">
            Using our debt recycling strategy.
          </p>
          <p className="text-lg text-gs-text-soft max-w-3xl">
            Debt recycling is the deliberate, structured process of replacing non-deductible debt
            (your home loan) with deductible debt (an investment loan), slowly, over years. Your
            total borrowing doesn&apos;t increase. Only its character changes. Done well, it lets the
            tax office quietly share the cost of building your investment portfolio alongside
            paying down your home.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-3.5 rounded-lg font-semibold text-sm"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Book a strategy call
            </button>
            <a
              href="#how-it-works"
              className="px-6 py-3.5 rounded-lg font-semibold text-sm border border-[#D8D8D0] hover:bg-white transition-colors"
            >
              See how it works
            </a>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="max-w-6xl mx-auto px-6 pb-16" style={{ scrollMarginTop: '100px' }}>
          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">The mechanic</div>
            <h2 className="font-heading text-3xl md:text-4xl text-ink">How it works</h2>
          </div>
          <div
            className="rounded-2xl p-6 md:p-8 grid gap-6 md:grid-cols-4"
            style={{ background: tenantConfig.primaryColor }}
          >
            {STEPS.map((s) => (
              <div key={s.n} className="text-white/90">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-lg mb-3"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                >
                  {s.n}
                </div>
                <h3 className="font-heading font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-white/70">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PROS + CONS */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="mb-8">
            <h2 className="font-heading text-3xl md:text-4xl text-ink">Pros and cons</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl p-6" style={{ background: '#F4FBD9' }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-ink mb-4">Pros</div>
              <ul className="space-y-3">
                {PROS.map((p) => (
                  <li key={p} className="flex gap-2 text-sm text-gs-text">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#9FC91F' }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl p-6" style={{ background: '#FEE2E2' }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-red-800 mb-4">Cons</div>
              <ul className="space-y-3">
                {CONS.map((c) => (
                  <li key={c} className="flex gap-2 text-sm text-gs-text">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-red-500" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* WHAT IT COULD LOOK LIKE */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="mb-8">
            <h2 className="font-heading text-3xl md:text-4xl text-ink">What it could look like</h2>
            <p className="text-gs-text-soft mt-3 max-w-3xl">
              A household with a $600,000 home loan and $50,000 of annual surplus cash flow available
              for debt acceleration. At a 37% marginal tax rate, over roughly 10 years of consistent
              recycling, the entire home loan balance can be converted into deductible investment debt,
              without any increase in total borrowing.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <StatBlock big="$600K" label="Starting home loan" />
            <StatBlock big="$50K/yr" label="Surplus cash flow" />
            <StatBlock big="~10 yrs" label="Until fully recycled" highlight />
          </div>
          <p className="text-xs text-gs-text-muted mt-4">
            Illustrative only. Real outcomes depend on your specific cash flow, interest rates,
            investment returns and tax position. Investment markets may not match historical patterns.
          </p>
        </section>

        {/* WHO IT SUITS */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="mb-8">
            <h2 className="font-heading text-3xl md:text-4xl text-ink">Who it suits, and who it doesn&apos;t</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl p-6" style={{ background: '#F4FBD9' }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-ink mb-4">Good fit</div>
              <ul className="space-y-3">
                {GOOD_FIT.map((g) => (
                  <li key={g} className="flex gap-2 text-sm text-gs-text">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#9FC91F' }} />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl p-6" style={{ background: '#FEE2E2' }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-red-800 mb-4">Not for you if</div>
              <ul className="space-y-3">
                {NOT_FIT.map((n) => (
                  <li key={n} className="flex gap-2 text-sm text-gs-text">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-red-500" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* WHAT TO WATCH */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="mb-8">
            <h2 className="font-heading text-3xl md:text-4xl text-ink">What to watch</h2>
            <p className="text-gs-text-soft mt-3 max-w-3xl">
              Debt recycling is mechanically simple but operationally unforgiving. The strategy only
              works if four things are right: the loan structure, the use of funds, the investment
              choice, and the discipline to keep going. Get any one of them wrong and the case unravels.
            </p>
          </div>
          <div className="rounded-2xl border border-[#E8E8E2] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F0] text-left text-xs uppercase tracking-wide text-gs-text-soft">
                <tr>
                  <th className="px-4 py-3 w-40">Area</th>
                  <th className="px-4 py-3">What to get right</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E8E2]">
                {WATCH.map((w) => (
                  <tr key={w.area}>
                    <td className="px-4 py-3 font-medium text-ink">{w.area}</td>
                    <td className="px-4 py-3 text-gs-text-soft">{w.body}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* POST-BUDGET 2026 */}
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <div className="mb-6">
            <h2 className="font-heading text-3xl md:text-4xl text-ink">Post-Budget 2026</h2>
          </div>
          <p className="text-gs-text-soft leading-relaxed">
            The 2026 Federal Budget changed the relative attractiveness of different destinations for
            recycled debt. <strong className="text-ink">Shares and ETFs remain entirely unaffected</strong> by the negative gearing
            reforms, investments in non-property assets are unchanged. <strong className="text-ink">New builds retain full negative
            gearing and CGT optionality</strong>, making them a strong destination for recycled debt going
            forward. <strong className="text-ink">Established residential property purchased after Budget night</strong> (7:30pm AEST,
            12 May 2026) loses the wage-offset benefit, weakening the case for directing recycled debt
            at established residential investment. The FY26&ndash;FY27 CGT window also opens an additional
            move: realising existing embedded gains under the current 50% discount and redeploying
            proceeds via deductible-debt-funded investment before the new CGT regime begins.
          </p>
        </section>

        {/* CTA BAND */}
        <section className="max-w-6xl mx-auto px-6">
          <div
            className="rounded-3xl p-10 md:p-14 grid gap-6 md:grid-cols-[1.5fr_1fr] items-center"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div>
              <h2 className="font-heading text-3xl md:text-4xl mb-3" style={{ color: tenantConfig.accentColor }}>
                Should you be recycling your debt?
              </h2>
              <p className="text-white/80 text-lg">
                We&apos;ll model your numbers, your structure and your tax position, and tell you straight
                whether it stacks up. We coordinate with your accountant, not replace them.
              </p>
            </div>
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-4 rounded-lg font-semibold text-base w-full md:w-auto justify-self-start md:justify-self-end"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Book a strategy call
            </button>
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="max-w-4xl mx-auto px-6 mt-12">
          <p className="text-xs text-gs-text-muted text-center">
            General information only. Not personal tax, legal or financial advice. Seek tailored advice.
          </p>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        source="website"
      />
    </>
  )
}

function StatBlock({ big, label, highlight = false }: { big: string; label: string; highlight?: boolean }) {
  return (
    <div
      className="rounded-2xl p-6 text-center"
      style={{
        background: highlight ? tenantConfig.accentColor : '#F4F4F0',
        color: highlight ? tenantConfig.primaryColor : undefined,
      }}
    >
      <div className="font-heading text-4xl md:text-5xl font-bold tab-num">{big}</div>
      <div className={`text-sm mt-2 ${highlight ? 'text-ink/70' : 'text-gs-text-muted'}`}>{label}</div>
    </div>
  )
}
